'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef } from 'react';
import {
  useTradingDataLoading,
  useTradingTradeHistory,
} from '@/lib/components/Trading/TradingDataContext';
import { useSelectedOutcome } from '@/lib/store/order-form-store';
import { InlineEmpty } from '@/lib/ui-kit/inline-empty';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';
import { formatTimeAgo } from '@/lib/utils/formatTime';

const HISTORY_ROW_HEIGHT = 44;

type HistoryRow = {
  id: string;
  action: 'Sold' | 'Bought';
  quantity: number;
  outcome: 'Yes' | 'No';
  price: number; // price in cents (0-100)
  total: number; // total amount in dollars
  timeAgo: string;
};

export const EventHistoryTable = () => {
  const t = useTranslations('history');
  const selectedOutcome = useSelectedOutcome();
  const outcomeId = selectedOutcome?.id ?? '';
  const resolutions = selectedOutcome?.r; // Array of Resolution objects with id and name

  // ✅ Use shared data from TradingDataContext (no duplicate requests)
  const data = useTradingTradeHistory(outcomeId);
  const isLoading = useTradingDataLoading(outcomeId);

  const rows: HistoryRow[] = useMemo(() => {
    if (!data?.content) return [];

    return data.content
      .filter((trade) => trade.quantity > 0) // Filter out trades with 0 quantity (invalid data)
      .map((trade) => {
        const priceInCents = new Decimal(trade.price).mul(100).toNumber();
        const total = new Decimal(trade.price).mul(trade.quantity).toNumber();
        // Parse matchTime as UTC (ISO string should be in UTC)
        // Ensure the date is treated as UTC by appending 'Z' if not present
        const matchTimeUTC = trade.matchTime.endsWith('Z')
          ? trade.matchTime
          : trade.matchTime + 'Z';
        const matchDate = new Date(matchTimeUTC);
        // Use UTC time for now as well
        const nowUTC = new Date();
        const timeAgo = formatTimeAgo(matchDate, true, nowUTC);

        // Backend sends `s: "BUY" | "SELL"` per row (this user's side), which the
        // mapper still surfaces as `trade.sellerId` for legacy reasons.
        const action: 'Sold' | 'Bought' = trade.sellerId === 'SELL' ? 'Sold' : 'Bought';

        // WS rows carry position directly; REST rows derive it from assetId.
        let outcome: 'Yes' | 'No' = trade.position ?? 'Yes';
        if (!trade.position && resolutions && trade.assetId) {
          const resolution = resolutions.find((r) => r.id === trade.assetId);
          if (resolution) {
            outcome = resolution.isPositive ? 'Yes' : 'No';
          }
        }

        return {
          id: trade.id,
          action,
          quantity: trade.quantity,
          outcome,
          price: priceInCents,
          total,
          timeAgo,
        };
      });
  }, [data, resolutions]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1 px-4 py-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between px-0 py-3">
            <Skeleton className="h-4 w-[260px] bg-v2-btn-secondary-2" />
            <Skeleton className="h-4 w-14 bg-v2-btn-secondary-2" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <InlineEmpty>{t('noHistory')}</InlineEmpty>
      </div>
    );
  }

  return <VirtualizedHistory rows={rows} t={t} />;
};

type VirtualizedHistoryProps = {
  rows: HistoryRow[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
};

// Virtualized list — keeps DOM cheap when history grows. Mirrors the OrderBook
// virtualizer pattern: stable callbacks via refs to avoid the
// @tanstack/virtual-core `getMeasurementOptions` re-render loop.
const VirtualizedHistory = ({ rows, t }: VirtualizedHistoryProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const getScrollElement = useCallback(() => scrollRef.current, []);
  const estimateSize = useCallback(() => HISTORY_ROW_HEIGHT, []);
  const getItemKey = useCallback((index: number) => {
    const row = rowsRef.current[index];
    return row !== undefined ? row.id : index;
  }, []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize,
    overscan: 6,
    getItemKey,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <div ref={scrollRef} className="max-h-[300px] overflow-y-auto scrollbar-hide">
      <div className="relative w-full" style={{ height: `${totalSize}px` }}>
        {virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (row === undefined) return null;
          return (
            <div
              key={virtualRow.key}
              className="absolute inset-x-0 flex items-center justify-between px-4 py-3"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="flex items-center gap-1 text-[13px] leading-[1.5]">
                <span className="text-v2-text-primary">
                  {row.action === 'Sold' ? t('sold') : t('bought')}
                </span>
                <span className={cn(row.outcome === 'Yes' ? 'text-v2-green' : 'text-red-400')}>
                  {row.quantity} {row.outcome}
                </span>
                <span className="text-v2-text-primary">
                  {t('at')} {formatCents(row.price)}
                </span>
                <span className="text-v2-text-secondary">
                  ({formatMoney(row.total, { prefix: '$' })})
                </span>
              </div>
              <span className="text-[13px] leading-[1.5] text-v2-text-secondary whitespace-nowrap">
                {t('ago', { time: row.timeAgo })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
